import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoJsonLd from '@/components/base/SeoJsonLd';

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  lastUpdated: string;
  readTime: string;
  tableOfContents: string[];
  children: React.ReactNode;
  relatedCategories?: { name: string; url: string }[];
}

export default function ArticleLayout({
  title,
  subtitle,
  lastUpdated,
  readTime,
  tableOfContents,
  children,
  relatedCategories,
}: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const location = useLocation();
  const siteOrigin = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.hair';
  const pageUrl = `${siteOrigin}${location.pathname}`;
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: subtitle,
      url: pageUrl,
      mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
      datePublished: '2026-08-18',
      dateModified: '2026-08-18',
      author: { '@type': 'Organization', name: 'Basetao Sheet Knowledge Hub' },
      publisher: { '@type': 'Organization', name: 'Basetao Sheet Knowledge Hub', url: siteOrigin },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteOrigin}/` },
        { '@type': 'ListItem', position: 2, name: 'Shopping Guide', item: `${siteOrigin}/shopping-guide` },
        { '@type': 'ListItem', position: 3, name: title, item: pageUrl },
      ],
    },
  ];

  const categoryLinks = relatedCategories || [
    { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
    { name: 'Hoodies & Sweaters', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
    { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
    { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
    { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
    { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <SeoJsonLd data={structuredData} />

      <main>
        <article>
          <header className="w-full pt-24 pb-16 md:pt-32 md:pb-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
                <span>/</span>
                <Link to="/shopping-guide" className="hover:text-primary-500 transition-colors">Shopping Guide</Link>
                <span>/</span>
                <span className="text-foreground-700 truncate">{title}</span>
              </div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wide mb-5">
                Pillar Article &bull; 2026 Edition
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                {title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl">
                {subtitle}
              </p>
              <div className="flex items-center gap-4 mt-6 text-xs text-foreground-500">
                <span>Last Updated: {lastUpdated}</span>
                <span>&bull;</span>
                <span>{readTime} min read</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex flex-col lg:flex-row gap-12">
              <aside className="lg:w-64 flex-shrink-0">
                <div className="lg:sticky lg:top-24">
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 uppercase tracking-wide mb-4">
                    Table of Contents
                  </h4>
                  <nav className="space-y-2">
                    {tableOfContents.map((item) => (
                      <a
                        key={item}
                        href={`#${item.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                        className="block text-sm text-foreground-600 hover:text-primary-500 transition-colors py-1 leading-snug"
                      >
                        {item}
                      </a>
                    ))}
                  </nav>

                  <div className="mt-8 p-4 rounded-xl bg-accent-50 border border-accent-200/50">
                    <p className="text-xs font-semibold text-accent-700 mb-3">
                      Quick Links
                    </p>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="block text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors mb-2"
                    >
                      Basetao Spreadsheet &rarr;
                    </a>
                    <Link
                      to="/"
                      className="block text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors mb-2"
                    >
                      Back to Homepage &rarr;
                    </Link>
                    <Link
                      to="/shopping-guide"
                      className="block text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors"
                    >
                      All Guides &rarr;
                    </Link>
                  </div>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="prose-custom space-y-14">
                  {children}
                </div>

                <div className="mt-12 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-3">
                    Explore Product Categories
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {categoryLinks.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-xs font-medium text-foreground-700 hover:text-primary-600 hover:border-primary-300 transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="mt-8 p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
                    Ready to Start Shopping?
                  </h3>
                  <p className="text-sm text-foreground-600 mb-5">
                    Explore the full Basetao Spreadsheet with all product categories, pricing details, and community reviews.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
                    >
                      Basetao Spreadsheet
                    </a>
                    <Link
                      to="/"
                      className="px-6 py-3 rounded-lg bg-background-50 border border-background-300 text-foreground-700 text-sm font-semibold hover:bg-background-200/50 transition-colors text-center whitespace-nowrap"
                    >
                      Back to Homepage
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}